/**
 * KeyManagementService — Centralised key management
 *
 * Covers TODO Phase 13.1 — Key Management:
 *  - In production: envelope encryption via AWS KMS (data-key encrypted by KMS master key)
 *  - Separate key for TFN fields (Australian Tax File Number)
 *  - No keys stored in code, env vars, or localStorage
 *  - Keys held only in memory for the lifetime of the session
 *
 * In demo/development mode keys are derived deterministically from the
 * authenticated user's session token so that data survives page reloads.
 */

import CryptoJS from 'crypto-js';

const KMS_ENDPOINT = process.env.REACT_APP_KMS_ENDPOINT || '/api/kms';
const isProduction = process.env.NODE_ENV === 'production';

class KeyManagementService {
  constructor() {
    this._masterKey = null;
    this._dataKey = null;
    this._tfnKey = null;
    this._keyId = null;
    this._rotatedAt = null;
    this._ready = false;
  }

  // ── Async initialisation (preferred — called early in app boot) ───

  async getDataKeys() {
    if (this._ready) {
      return { dataKey: this._dataKey, tfnKey: this._tfnKey };
    }

    if (isProduction) {
      await this._fetchFromKMS();
    } else {
      this._deriveFromSession();
    }

    this._ready = true;
    this._rotatedAt = new Date().toISOString();
    return { dataKey: this._dataKey, tfnKey: this._tfnKey };
  }

  getDataKeysSync() {
    if (this._ready) {
      return { dataKey: this._dataKey, tfnKey: this._tfnKey };
    }
    this._deriveFromSession();
    this._ready = true;
    this._rotatedAt = new Date().toISOString();
    return { dataKey: this._dataKey, tfnKey: this._tfnKey };
  }

  // ── Production: AWS KMS envelope encryption ───────────────────────

  async _fetchFromKMS() {
    try {
      const response = await fetch(`${KMS_ENDPOINT}/generate-data-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this._getAccessToken()}`,
        },
        body: JSON.stringify({ keySpec: 'AES_256', context: { app: 'medfast-aged-care' } }),
      });

      if (!response.ok) {
        throw new Error(`KMS responded ${response.status}`);
      }

      const { dataKey, tfnKey, keyId } = await response.json();
      this._dataKey = CryptoJS.enc.Base64.parse(dataKey);
      this._tfnKey = CryptoJS.enc.Base64.parse(tfnKey);
      this._keyId = keyId;

      if (process.env.NODE_ENV === 'development') {
        console.debug('[KMS] Keys fetched from KMS, keyId:', keyId);
      }
    } catch (err) {
      console.error('[KMS] Failed to fetch keys — falling back to session-derived keys', err);
      this._deriveFromSession();
    }
  }

  // ── Development: derive from session token ────────────────────────

  _deriveFromSession() {
    const sessionToken = this._getSessionToken();
    const salt = 'medfast-aged-care-v1';

    this._dataKey = CryptoJS.PBKDF2(sessionToken, salt, {
      keySize: 256 / 32,
      iterations: 10000,
      hasher: CryptoJS.algo.SHA256,
    });

    this._tfnKey = CryptoJS.PBKDF2(sessionToken, `${salt}-tfn`, {
      keySize: 256 / 32,
      iterations: 10000,
      hasher: CryptoJS.algo.SHA256,
    });

    this._keyId = 'session-derived';
  }

  // ── Key rotation ──────────────────────────────────────────────────

  async rotateKeys() {
    this._ready = false;
    this._dataKey = null;
    this._tfnKey = null;

    if (isProduction) {
      await this._fetchFromKMS();
    } else {
      this._deriveFromSession();
    }

    this._ready = true;
    this._rotatedAt = new Date().toISOString();

    if (process.env.NODE_ENV === 'development') {
      console.debug('[KMS] Keys rotated at', this._rotatedAt);
    }

    return { keyId: this._keyId, rotatedAt: this._rotatedAt };
  }

  // ── Destroy keys on logout ────────────────────────────────────────

  destroy() {
    this._masterKey = null;
    this._dataKey = null;
    this._tfnKey = null;
    this._keyId = null;
    this._ready = false;

    if (process.env.NODE_ENV === 'development') {
      console.debug('[KMS] All keys destroyed');
    }
  }

  // ── Status ────────────────────────────────────────────────────────

  getStatus() {
    return {
      ready: this._ready,
      keyId: this._keyId,
      rotatedAt: this._rotatedAt,
      source: isProduction ? 'aws-kms' : 'session-derived',
    };
  }

  // ── Internal helpers ──────────────────────────────────────────────

  _getSessionToken() {
    try {
      const raw = window.sessionStorage.getItem('medfast.session.v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.id || parsed.username || 'anonymous';
      }
    } catch { /* noop */ }
    return 'demo-session-' + Date.now();
  }

  _getAccessToken() {
    try {
      const raw = window.sessionStorage.getItem('medfast.access_token');
      if (raw) return raw;
    } catch { /* noop */ }
    return '';
  }
}

const instance = new KeyManagementService();
export default instance;
